import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useMixpanel } from 'react-mixpanel-browser';
import { useDispatch, useSelector } from 'react-redux';

import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { Envelope, EnvelopeWithStatus } from 'types/Envelope';

import { useTranslate } from 'hooks/useTranslate';
import invitationStatus from 'constants/invitationStatus';

import menuOptions from 'modules/envelopes/pages/EnvelopesList/utils/menuOptions';
import { addAlert } from 'modules/_shared/redux/alertActions';
import alertBodyTypes from 'modules/_shared/components/Alert/alertBodyTypes';
import {
  cancelEnvelope,
  sendEnvelopeReminder,
} from 'modules/_shared/redux/envelopeActions';
import { EnvelopeStatus } from 'modules/envelopes/components/EnvelopesTable/envelopeStatus';
import { updateBeneficiary } from 'modules/beneficiaries/redux/beneficiaryActions';
import { getEnvelopeDetailsUrl } from 'modules/envelopes/utils/getEnvelopeDetailsUrl';
import { getEnvelopeSignatureId } from 'modules/envelopes/utils/getEnvelopeSignatureId';

import successSwal from 'utils/successSwal';
import { hasRefreshStatus } from 'utils/showDetails';

type UseEnvelopesListRecentsSectionRowProps = {
  envelope: EnvelopeWithStatus;
  envelopes: Envelope[];
  onRefresh?: () => Promise<void>;
};

export const useEnvelopesListRecentsSectionRow = ({
  envelope,
  envelopes,
  onRefresh,
}: UseEnvelopesListRecentsSectionRowProps) => {
  const { t, i18n } = useTranslate();
  const history = useHistory();
  const dispatch = useDispatch();
  const mixpanel = useMixpanel();

  const user = useSelector((state: State) => state.user);
  const { isAdmin } = useSelector((state: State) => state.society?.role || {});
  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [currentMenuOptions] = useState(menuOptions(i18n));

  const isEnvelopeNotCancelable = [
    EnvelopeStatus.DECLINED,
    EnvelopeStatus.VOIDED,
    EnvelopeStatus.EXPIRED,
    EnvelopeStatus.DELETED,
  ].includes(envelope.envelopeStatus?.envelopeStatus as EnvelopeStatus);

  const isEnvelopeNotResendable = [
    EnvelopeStatus.CREATED,
    EnvelopeStatus.SIGNED,
    EnvelopeStatus.COMPLETED,
    EnvelopeStatus.DECLINED,
    EnvelopeStatus.VOIDED,
    EnvelopeStatus.EXPIRED,
    EnvelopeStatus.CORRECT,
    EnvelopeStatus.TEMPLATE,
    EnvelopeStatus.DELETED,
    EnvelopeStatus.AUTHORITATIVE_COPY,
    EnvelopeStatus.TRANSFER_COMPLETED,
    EnvelopeStatus.TIMEOUT,
    EnvelopeStatus.NO_STATUS,
  ].includes(envelope.envelopeStatus?.envelopeStatus as EnvelopeStatus);

  const isEnvelopeRefreshable = [EnvelopeStatus.COMPLETED].includes(
    envelope.envelopeStatus?.envelopeStatus as EnvelopeStatus
  );

  const isResendButtonDisabled =
    !envelope.envelopeStatus?.signersStatus.shouldResend ||
    isEnvelopeNotResendable;

  const isCancelButtonDisabled =
    envelope.envelopeStatus?.signersStatus.allSigned || isEnvelopeNotCancelable;

  const isRefreshButtonDisabled =
    !isEnvelopeRefreshable ||
    !hasRefreshStatus() ||
    envelope.type !== 'INVITATION_LETTER';

  const handleClickResend = async () => {
    if (!envelope) return;

    setIsLoading(true);
    setIsError(false);

    try {
      const envelopeSignatureId = getEnvelopeSignatureId(envelope);
      await sendEnvelopeReminder(envelope._id, envelopeSignatureId);

      if (onRefresh) {
        await onRefresh();
      }
      dispatch(addAlert(alertBodyTypes.ENVELOPE_REMINDER_SENT_SUCCESSFULLY));
    } catch (error) {
      setIsError(true);
      dispatch(addAlert(alertBodyTypes.ERROR_SENDING_ENVELOPE_REMINDER));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClickCancel = async () => {
    if (!envelope) return;

    setIsLoading(true);
    setIsError(false);

    try {
      const envelopeSignatureId = getEnvelopeSignatureId(envelope);
      await cancelEnvelope(envelope._id, envelopeSignatureId);

      if (onRefresh) {
        await onRefresh();
      }
      dispatch(addAlert(alertBodyTypes.ENVELOPE_CANCELED_SUCCESSFULLY));
    } catch (error) {
      setIsError(true);
      dispatch(addAlert(alertBodyTypes.ERROR_CANCELING_ENVELOPE));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClickSubject = (envelope: EnvelopeWithStatus) => {
    const matchingEnvelope = envelopes.find(
      (env) =>
        env.envelopeId === envelope?.envelopeStatus?.envelopeInfo?.envelopeId
    );

    if (matchingEnvelope) {
      const detailsUrl = getEnvelopeDetailsUrl(matchingEnvelope);
      history.push(detailsUrl);
    }
  };

  const handleClickRefresh = async () => {
    if (!envelope) return;

    setIsLoading(true);
    setIsError(false);

    try {
      const beneficiary = envelope.signers.find((signer) =>
        signer.redirectUrl.includes('detalle-beneficiario')
      );
      const beneficiaryId = beneficiary?.id;

      if (!beneficiaryId) return;

      const updatedBeneficiary = {
        invitationStatus: invitationStatus.ACCEPTED.value,
      };

      await store.dispatch(
        updateBeneficiary({
          id: beneficiaryId,
          data: {
            ...updatedBeneficiary,
            userId: user._id,
            societyId: actualSociety._id,
            cascade: false,
          },
          showAlert: false,
        })
      );

      successSwal(t('SuccessfullyEditedStatusLetter'));
    } catch (error) {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const data = {
    user,
    actualSociety,
    isAdmin,
    currentMenuOptions,
    isEnvelopeNotCancelable,
    isEnvelopeNotResendable,
    isResendButtonDisabled,
    isCancelButtonDisabled,
    isRefreshButtonDisabled,
    mixpanel,
  };

  const actions = {
    handleClickResend,
    handleClickCancel,
    handleClickSubject,
    handleClickRefresh,
  };

  return {
    data,
    isLoading,
    isError,
    actions,
  };
};
